/**
 * HudState — typed bridge between world scenes (Overworld/Game) and UIScene.
 *
 * This module is the ONLY sanctioned place that reads/writes `game.registry`
 * or emits/listens for `game.events` on behalf of HUD panels. Panels never
 * touch the registry or event emitter directly — they go through helpers
 * exported here.
 *
 * Why: typo-proofing registry keys, grep-ability, and a single schema that
 * future contributors (and future-us) can look at to see what UIScene reads.
 *
 * Grows additively as panels migrate — see docs/design/scene-refactor-plan.md.
 */
import type * as Phaser from 'phaser';
import type { AudioSettings } from '../audio/AudioManager';

// ── Audio gateway ───────────────────────────────────────────────────────────
// The Settings panel (in UIScene) needs to read/write audio levels and
// persist them, but each world scene has its OWN audio manager
// (AudioManager in GameScene, OverworldAudioManager in OverworldScene).
// The active world scene publishes an AudioGateway to the registry on
// create() and clears it on shutdown(); UIScene reads the gateway lazily at
// every slider interaction so it always talks to the currently-active
// scene's audio pipeline.
export interface AudioGateway {
  getSettings(): AudioSettings;
  setMasterVolume(v: number): void;
  setSfxVolume(v: number): void;
  setMusicVolume(v: number): void;
  persist(): void;
}

// ── Registry keys ───────────────────────────────────────────────────────────
// Each key is a *snapshot* of state that a newly-mounted panel needs to read
// immediately to render correctly (e.g. current health, current realm). Use
// events (below) for one-shot edges, not for state.
export const HudRegistryKeys = {
  AUDIO_GATEWAY: 'audio.gateway',
  INVENTORY_SNAPSHOT: 'inventory.snapshot',
  /** Latest player vitals snapshot, so a HUD mounted mid-session renders
   *  correctly before the next VITALS_CHANGED edge arrives (C5 Layer 0). */
  HUD_VITALS: 'hud.vitals',
} as const;

export type HudRegistryKey =
  (typeof HudRegistryKeys)[keyof typeof HudRegistryKeys];

// ── Events ──────────────────────────────────────────────────────────────────
// Each event is an *edge*: something happened, once. Panels listen on
// `game.events`; world scenes emit on `game.events`. Use the registry (above)
// for persistent state.
export const HudEvents = {
  /** World scene → UIScene: show the pause overlay. */
  PAUSE_SHOW: 'hud:pause-show',
  /** World scene → UIScene: hide the pause overlay (e.g. phase transition). */
  PAUSE_HIDE: 'hud:pause-hide',
  /** UIScene → world scene: user clicked Resume on the pause overlay. */
  PAUSE_RESUMED: 'hud:pause-resumed',
  /** UIScene → world scene: user clicked Quit on the pause overlay.
   *  Behavior differs per world scene (GameScene returns to room browser,
   *  OverworldScene reloads the page). */
  PAUSE_QUIT: 'hud:pause-quit',
  /** World scene → UIScene: hide the settings panel without any other
   *  side effect (e.g. phase/lobby transition where we just want settings
   *  gone). */
  SETTINGS_HIDE: 'hud:settings-hide',
  /** World scene → UIScene: close settings AND reshow the pause overlay.
   *  Used when the user presses Escape while the settings panel is open. */
  SETTINGS_CLOSE: 'hud:settings-close',
  /** UIScene → world scene: settings panel just became visible. World
   *  scenes mirror this into a local isSettingsOpen flag so their Escape
   *  handlers can dispatch the right command. */
  SETTINGS_OPENED: 'hud:settings-opened',
  /** UIScene → world scene: settings panel just became hidden. */
  SETTINGS_CLOSED: 'hud:settings-closed',
  /** World scene → UIScene: toggle the inventory panel. */
  INVENTORY_TOGGLE: 'hud:inventory-toggle',
  /** World scene → UIScene: hide inventory (e.g. pause, zone transition). */
  INVENTORY_HIDE: 'hud:inventory-hide',
  /** UIScene → world scene: inventory panel just became visible. */
  INVENTORY_OPENED: 'hud:inventory-opened',
  /** UIScene → world scene: inventory panel just became hidden. */
  INVENTORY_CLOSED: 'hud:inventory-closed',
  /** World scene → UIScene: toggle the material inventory panel. */
  MATERIAL_INVENTORY_TOGGLE: 'hud:material-inventory-toggle',
  /** World scene → UIScene: hide material inventory (e.g. pause, zone transition). */
  MATERIAL_INVENTORY_HIDE: 'hud:material-inventory-hide',
  /** UIScene → world scene: touch controls visibility changed (AGE-955). */
  TOUCH_CONTROLS_CHANGED: 'hud:touch-controls-changed',
  /** World scene → UIScene: toggle the hearth chat panel. */
  HEARTH_CHAT_TOGGLE: 'hud:hearth-chat-toggle',
  /** World scene → UIScene: hide hearth chat (e.g. zone transition). */
  HEARTH_CHAT_HIDE: 'hud:hearth-chat-hide',
  /** World scene → UIScene: a hearth whisper message arrived. */
  HEARTH_CHAT_MESSAGE: 'hud:hearth-chat-message',
  /** UIScene → world scene: user submitted a whisper message. */
  HEARTH_CHAT_SEND: 'hud:hearth-chat-send',
  /** UIScene → world scene: hearth chat panel became visible. */
  HEARTH_CHAT_OPENED: 'hud:hearth-chat-opened',
  /** UIScene → world scene: hearth chat panel became hidden. */
  HEARTH_CHAT_CLOSED: 'hud:hearth-chat-closed',
  /** UIScene → world scene: hearth chat input is focused (suppress game keys). */
  HEARTH_CHAT_INPUT_FOCUSED: 'hud:hearth-chat-input-focused',
  /** UIScene → world scene: hearth chat input lost focus. */
  HEARTH_CHAT_INPUT_BLURRED: 'hud:hearth-chat-input-blurred',
  /** UIScene / PaintedScene → any: debug overlay visibility changed. */
  DEBUG_OVERLAY_CHANGED: 'hud:debug-overlay-changed',

  // ── C5 "Indigo & Ink" frame (Layer 0) ────────────────────────────────────
  // The DOM frame lives outside every Phaser scene, so world scenes publish
  // their state here and the frame subscribes. See docs/design/ui-c5-indigo-ink.md.

  /** World scene → C5 frame: player vitals changed. */
  VITALS_CHANGED: 'hud:vitals-changed',
  /** World scene → C5 frame: day/night clock tick (throttled, ~4/s). */
  TIME_TICK: 'hud:time-tick',
  /** World scene → C5 frame: contextual hint changed; null clears the slip. */
  HINT_CHANGED: 'hud:hint-changed',
  /** Sleep flow → C5 frame: a new day began. */
  DAY_CHANGED: 'hud:day-changed',
  /** World scene → C5 frame: the specimen label text (scene display name). */
  SCENE_LABEL_CHANGED: 'hud:scene-label-changed',
  /**
   * Scene → C5 frame: how much of the console this scene wants around it.
   *
   * `full` is the Indigo & Ink frame as designed — bands, vitals, sockets, the
   * day-wheel. `bare` strips all of it and gives the painting the space back,
   * for a scene that IS its own interface.
   */
  CHROME_MODE_CHANGED: 'hud:chrome-mode-changed',
  /** Any system → C5 frame: show a loose paper slip on the painting corner. */
  SLIP_SHOW: 'hud:slip-show',

  // ── Letterbox (C5 Layer 3) ───────────────────────────────────────────────
  /** Letterbox → any: a letter arrived in the box today. */
  LETTER_ARRIVED: 'hud:letter-arrived',
  /** Letterbox → any: the traveler opened a letter (first read or archive). */
  LETTER_OPENED: 'hud:letter-opened',
  /** Any system → Letterbox: open/close the letterbox (socket click, key). */
  LETTERBOX_TOGGLE: 'hud:letterbox-toggle',
  /** Any system → C5 almanac: open the almanac if closed, close it if open.
   *  The day-wheel click and the almanac key both route through this. */
  ALMANAC_TOGGLE: 'hud:almanac-toggle',
  /** C5 almanac → world scenes: the paper spread is open (suppress world keys). */
  ALMANAC_OPENED: 'hud:almanac-opened',
  /** C5 almanac → world scenes: the paper spread is closed. */
  ALMANAC_CLOSED: 'hud:almanac-closed',
} as const;

export type HudEvent = (typeof HudEvents)[keyof typeof HudEvents];

/** Unsubscriber returned by every `onX` helper. Call to remove the listener.
 *  Store the return value and invoke it in `shutdown()` to honor the
 *  lifecycle contract from scene-refactor-plan.md §9. */
export type HudUnsub = () => void;

// ── Pause event helpers ─────────────────────────────────────────────────────
// World scenes emit show/hide commands; UIScene emits user-intent events
// back. Every `onX` helper returns an unsubscriber so callers can tear down
// cleanly in their `shutdown()`.

export function emitPauseShow(game: Phaser.Game): void {
  game.events.emit(HudEvents.PAUSE_SHOW);
}

export function emitPauseHide(game: Phaser.Game): void {
  game.events.emit(HudEvents.PAUSE_HIDE);
}

export function onPauseShow(game: Phaser.Game, cb: () => void): HudUnsub {
  game.events.on(HudEvents.PAUSE_SHOW, cb);
  return () => game.events.off(HudEvents.PAUSE_SHOW, cb);
}

export function onPauseHide(game: Phaser.Game, cb: () => void): HudUnsub {
  game.events.on(HudEvents.PAUSE_HIDE, cb);
  return () => game.events.off(HudEvents.PAUSE_HIDE, cb);
}

export function emitPauseResumed(game: Phaser.Game): void {
  game.events.emit(HudEvents.PAUSE_RESUMED);
}

export function onPauseResumed(game: Phaser.Game, cb: () => void): HudUnsub {
  game.events.on(HudEvents.PAUSE_RESUMED, cb);
  return () => game.events.off(HudEvents.PAUSE_RESUMED, cb);
}

export function emitPauseQuit(game: Phaser.Game): void {
  game.events.emit(HudEvents.PAUSE_QUIT);
}

export function onPauseQuit(game: Phaser.Game, cb: () => void): HudUnsub {
  game.events.on(HudEvents.PAUSE_QUIT, cb);
  return () => game.events.off(HudEvents.PAUSE_QUIT, cb);
}

// ── Settings event helpers ──────────────────────────────────────────────────

export function emitSettingsHide(game: Phaser.Game): void {
  game.events.emit(HudEvents.SETTINGS_HIDE);
}

export function onSettingsHide(game: Phaser.Game, cb: () => void): HudUnsub {
  game.events.on(HudEvents.SETTINGS_HIDE, cb);
  return () => game.events.off(HudEvents.SETTINGS_HIDE, cb);
}

export function emitSettingsClose(game: Phaser.Game): void {
  game.events.emit(HudEvents.SETTINGS_CLOSE);
}

export function onSettingsClose(game: Phaser.Game, cb: () => void): HudUnsub {
  game.events.on(HudEvents.SETTINGS_CLOSE, cb);
  return () => game.events.off(HudEvents.SETTINGS_CLOSE, cb);
}

export function emitSettingsOpened(game: Phaser.Game): void {
  game.events.emit(HudEvents.SETTINGS_OPENED);
}

export function onSettingsOpened(game: Phaser.Game, cb: () => void): HudUnsub {
  game.events.on(HudEvents.SETTINGS_OPENED, cb);
  return () => game.events.off(HudEvents.SETTINGS_OPENED, cb);
}

export function emitSettingsClosed(game: Phaser.Game): void {
  game.events.emit(HudEvents.SETTINGS_CLOSED);
}

export function onSettingsClosed(game: Phaser.Game, cb: () => void): HudUnsub {
  game.events.on(HudEvents.SETTINGS_CLOSED, cb);
  return () => game.events.off(HudEvents.SETTINGS_CLOSED, cb);
}

// ── Audio gateway registry helpers ──────────────────────────────────────────

export function publishAudioGateway(
  game: Phaser.Game,
  gateway: AudioGateway,
): void {
  game.registry.set(HudRegistryKeys.AUDIO_GATEWAY, gateway);
}

export function clearAudioGateway(game: Phaser.Game): void {
  game.registry.remove(HudRegistryKeys.AUDIO_GATEWAY);
}

export function getAudioGateway(game: Phaser.Game): AudioGateway | null {
  const gw = game.registry.get(HudRegistryKeys.AUDIO_GATEWAY) as
    | AudioGateway
    | undefined;
  return gw ?? null;
}

// ── Inventory snapshot registry helpers ────────────────────────────────────

import type { InventorySnapshot } from '../ui/InventoryPanel';

export function publishInventorySnapshot(
  game: Phaser.Game,
  provider: () => InventorySnapshot | null,
): void {
  game.registry.set(HudRegistryKeys.INVENTORY_SNAPSHOT, provider);
}

export function clearInventorySnapshot(game: Phaser.Game): void {
  game.registry.remove(HudRegistryKeys.INVENTORY_SNAPSHOT);
}

export function getInventorySnapshotProvider(
  game: Phaser.Game,
): (() => InventorySnapshot | null) | null {
  const fn = game.registry.get(HudRegistryKeys.INVENTORY_SNAPSHOT) as
    | (() => InventorySnapshot | null)
    | undefined;
  return fn ?? null;
}

// ── Inventory event helpers ────────────────────────────────────────────────

export function emitInventoryToggle(game: Phaser.Game): void {
  game.events.emit(HudEvents.INVENTORY_TOGGLE);
}

export function emitInventoryHide(game: Phaser.Game): void {
  game.events.emit(HudEvents.INVENTORY_HIDE);
}

export function onInventoryToggle(game: Phaser.Game, cb: () => void): HudUnsub {
  game.events.on(HudEvents.INVENTORY_TOGGLE, cb);
  return () => game.events.off(HudEvents.INVENTORY_TOGGLE, cb);
}

export function onInventoryHide(game: Phaser.Game, cb: () => void): HudUnsub {
  game.events.on(HudEvents.INVENTORY_HIDE, cb);
  return () => game.events.off(HudEvents.INVENTORY_HIDE, cb);
}

export function emitInventoryOpened(game: Phaser.Game): void {
  game.events.emit(HudEvents.INVENTORY_OPENED);
}

export function onInventoryOpened(game: Phaser.Game, cb: () => void): HudUnsub {
  game.events.on(HudEvents.INVENTORY_OPENED, cb);
  return () => game.events.off(HudEvents.INVENTORY_OPENED, cb);
}

export function emitInventoryClosed(game: Phaser.Game): void {
  game.events.emit(HudEvents.INVENTORY_CLOSED);
}

export function onInventoryClosed(game: Phaser.Game, cb: () => void): HudUnsub {
  game.events.on(HudEvents.INVENTORY_CLOSED, cb);
  return () => game.events.off(HudEvents.INVENTORY_CLOSED, cb);
}

// ── Material inventory event helpers ──────────────────────────────────────

export function emitMaterialInventoryToggle(game: Phaser.Game): void {
  game.events.emit(HudEvents.MATERIAL_INVENTORY_TOGGLE);
}

export function emitMaterialInventoryHide(game: Phaser.Game): void {
  game.events.emit(HudEvents.MATERIAL_INVENTORY_HIDE);
}

export function onMaterialInventoryToggle(game: Phaser.Game, cb: () => void): HudUnsub {
  game.events.on(HudEvents.MATERIAL_INVENTORY_TOGGLE, cb);
  return () => game.events.off(HudEvents.MATERIAL_INVENTORY_TOGGLE, cb);
}

export function onMaterialInventoryHide(game: Phaser.Game, cb: () => void): HudUnsub {
  game.events.on(HudEvents.MATERIAL_INVENTORY_HIDE, cb);
  return () => game.events.off(HudEvents.MATERIAL_INVENTORY_HIDE, cb);
}

// ── Touch controls visibility (AGE-955) ────��─────��──────────────────────────

export function emitTouchControlsChanged(game: Phaser.Game, visible: boolean): void {
  game.events.emit(HudEvents.TOUCH_CONTROLS_CHANGED, visible);
}

export function onTouchControlsChanged(game: Phaser.Game, cb: (visible: boolean) => void): HudUnsub {
  game.events.on(HudEvents.TOUCH_CONTROLS_CHANGED, cb);
  return () => game.events.off(HudEvents.TOUCH_CONTROLS_CHANGED, cb);
}

// ── Hearth chat event helpers (Round 14 Phase E) ────────────────────────────

import type { HearthWhisperBroadcastEvent } from '@game/shared';

export function emitHearthChatToggle(game: Phaser.Game): void {
  game.events.emit(HudEvents.HEARTH_CHAT_TOGGLE);
}

export function emitHearthChatHide(game: Phaser.Game): void {
  game.events.emit(HudEvents.HEARTH_CHAT_HIDE);
}

export function emitHearthChatMessage(game: Phaser.Game, event: HearthWhisperBroadcastEvent): void {
  game.events.emit(HudEvents.HEARTH_CHAT_MESSAGE, event);
}

export function emitHearthChatSend(game: Phaser.Game, content: string): void {
  game.events.emit(HudEvents.HEARTH_CHAT_SEND, content);
}

export function emitHearthChatOpened(game: Phaser.Game): void {
  game.events.emit(HudEvents.HEARTH_CHAT_OPENED);
}

export function emitHearthChatClosed(game: Phaser.Game): void {
  game.events.emit(HudEvents.HEARTH_CHAT_CLOSED);
}

export function emitHearthChatInputFocused(game: Phaser.Game): void {
  game.events.emit(HudEvents.HEARTH_CHAT_INPUT_FOCUSED);
}

export function emitHearthChatInputBlurred(game: Phaser.Game): void {
  game.events.emit(HudEvents.HEARTH_CHAT_INPUT_BLURRED);
}

export function onHearthChatToggle(game: Phaser.Game, cb: () => void): HudUnsub {
  game.events.on(HudEvents.HEARTH_CHAT_TOGGLE, cb);
  return () => game.events.off(HudEvents.HEARTH_CHAT_TOGGLE, cb);
}

export function onHearthChatHide(game: Phaser.Game, cb: () => void): HudUnsub {
  game.events.on(HudEvents.HEARTH_CHAT_HIDE, cb);
  return () => game.events.off(HudEvents.HEARTH_CHAT_HIDE, cb);
}

export function onHearthChatMessage(game: Phaser.Game, cb: (event: HearthWhisperBroadcastEvent) => void): HudUnsub {
  game.events.on(HudEvents.HEARTH_CHAT_MESSAGE, cb);
  return () => game.events.off(HudEvents.HEARTH_CHAT_MESSAGE, cb);
}

export function onHearthChatSend(game: Phaser.Game, cb: (content: string) => void): HudUnsub {
  game.events.on(HudEvents.HEARTH_CHAT_SEND, cb);
  return () => game.events.off(HudEvents.HEARTH_CHAT_SEND, cb);
}

export function onHearthChatOpened(game: Phaser.Game, cb: () => void): HudUnsub {
  game.events.on(HudEvents.HEARTH_CHAT_OPENED, cb);
  return () => game.events.off(HudEvents.HEARTH_CHAT_OPENED, cb);
}

export function onHearthChatClosed(game: Phaser.Game, cb: () => void): HudUnsub {
  game.events.on(HudEvents.HEARTH_CHAT_CLOSED, cb);
  return () => game.events.off(HudEvents.HEARTH_CHAT_CLOSED, cb);
}

export function onHearthChatInputFocused(game: Phaser.Game, cb: () => void): HudUnsub {
  game.events.on(HudEvents.HEARTH_CHAT_INPUT_FOCUSED, cb);
  return () => game.events.off(HudEvents.HEARTH_CHAT_INPUT_FOCUSED, cb);
}

export function onHearthChatInputBlurred(game: Phaser.Game, cb: () => void): HudUnsub {
  game.events.on(HudEvents.HEARTH_CHAT_INPUT_BLURRED, cb);
  return () => game.events.off(HudEvents.HEARTH_CHAT_INPUT_BLURRED, cb);
}

// ── Debug overlay visibility ───────────────────────────────────────────────

export function emitDebugOverlayChanged(game: Phaser.Game, visible: boolean): void {
  game.events.emit(HudEvents.DEBUG_OVERLAY_CHANGED, visible);
}

export function onDebugOverlayChanged(game: Phaser.Game, cb: (visible: boolean) => void): HudUnsub {
  game.events.on(HudEvents.DEBUG_OVERLAY_CHANGED, cb);
  return () => game.events.off(HudEvents.DEBUG_OVERLAY_CHANGED, cb);
}

// ── C5 frame helpers (Layer 0) ─────────────────────────────────────────────

import type { TimeOfDay } from '@game/shared';

/** Player vitals as the C5 ink-card reads them. */
export interface HudVitalsPayload {
  health: number;
  maxHealth: number;
  mana: number;
  maxMana: number;
}

/** Throttled day/night clock sample for the day-wheel. */
export interface HudTimeTickPayload {
  dayTimeMs: number;
  /** Day fraction 0..1. */
  fraction: number;
  phase: TimeOfDay;
  /** Lunar phase 0..1 (0 = new, 0.5 = full). */
  moonPhase: number;
}

/** Contextual hint shown on the clipped slip. `key` is the key badge text. */
export interface HudHintPayload {
  key: string;
  label: string;
}

export interface HudDayPayload {
  day: number;
}

/** A loose paper slip (whisper, notice) drifting onto the painting. */
export interface HudSlipPayload {
  text: string;
}

export function emitVitalsChanged(game: Phaser.Game, vitals: HudVitalsPayload): void {
  game.registry.set(HudRegistryKeys.HUD_VITALS, vitals);
  game.events.emit(HudEvents.VITALS_CHANGED, vitals);
}

export function onVitalsChanged(
  game: Phaser.Game,
  cb: (vitals: HudVitalsPayload) => void,
): HudUnsub {
  game.events.on(HudEvents.VITALS_CHANGED, cb);
  return () => game.events.off(HudEvents.VITALS_CHANGED, cb);
}

/** Last published vitals, or null when nothing has been published yet. */
export function getVitals(game: Phaser.Game): HudVitalsPayload | null {
  const v = game.registry.get(HudRegistryKeys.HUD_VITALS) as HudVitalsPayload | undefined;
  return v ?? null;
}

export function emitTimeTick(game: Phaser.Game, tick: HudTimeTickPayload): void {
  game.events.emit(HudEvents.TIME_TICK, tick);
}

export function onTimeTick(
  game: Phaser.Game,
  cb: (tick: HudTimeTickPayload) => void,
): HudUnsub {
  game.events.on(HudEvents.TIME_TICK, cb);
  return () => game.events.off(HudEvents.TIME_TICK, cb);
}

export function emitHintChanged(game: Phaser.Game, hint: HudHintPayload | null): void {
  game.events.emit(HudEvents.HINT_CHANGED, hint);
}

export function onHintChanged(
  game: Phaser.Game,
  cb: (hint: HudHintPayload | null) => void,
): HudUnsub {
  game.events.on(HudEvents.HINT_CHANGED, cb);
  return () => game.events.off(HudEvents.HINT_CHANGED, cb);
}

export function emitDayChanged(game: Phaser.Game, day: number): void {
  const payload: HudDayPayload = { day };
  game.events.emit(HudEvents.DAY_CHANGED, payload);
}

export function onDayChanged(
  game: Phaser.Game,
  cb: (payload: HudDayPayload) => void,
): HudUnsub {
  game.events.on(HudEvents.DAY_CHANGED, cb);
  return () => game.events.off(HudEvents.DAY_CHANGED, cb);
}

/** How much console a scene wants drawn around it. */
export type ChromeMode = 'full' | 'bare';

export function emitChromeMode(game: Phaser.Game, mode: ChromeMode): void {
  game.events.emit(HudEvents.CHROME_MODE_CHANGED, mode);
}

export function onChromeMode(
  game: Phaser.Game,
  cb: (mode: ChromeMode) => void,
): HudUnsub {
  game.events.on(HudEvents.CHROME_MODE_CHANGED, cb);
  return () => game.events.off(HudEvents.CHROME_MODE_CHANGED, cb);
}

export function emitSceneLabelChanged(game: Phaser.Game, label: string | null): void {
  game.events.emit(HudEvents.SCENE_LABEL_CHANGED, label);
}

export function onSceneLabelChanged(
  game: Phaser.Game,
  cb: (label: string | null) => void,
): HudUnsub {
  game.events.on(HudEvents.SCENE_LABEL_CHANGED, cb);
  return () => game.events.off(HudEvents.SCENE_LABEL_CHANGED, cb);
}

export function emitSlipShow(game: Phaser.Game, text: string): void {
  const payload: HudSlipPayload = { text };
  game.events.emit(HudEvents.SLIP_SHOW, payload);
}

export function onSlipShow(
  game: Phaser.Game,
  cb: (payload: HudSlipPayload) => void,
): HudUnsub {
  game.events.on(HudEvents.SLIP_SHOW, cb);
  return () => game.events.off(HudEvents.SLIP_SHOW, cb);
}

// ── Letterbox helpers (C5 Layer 3) ─────────────────────────────────────────
// Letters carry no mechanics — these edges exist so ambient systems (audio,
// journal) can notice mail without reaching into the letterbox UI.

/** A letter, identified for listeners that only need to know "who, which". */
export interface HudLetterPayload {
  id: string;
  from: string;
}

export function emitLetterArrived(game: Phaser.Game, letter: HudLetterPayload): void {
  game.events.emit(HudEvents.LETTER_ARRIVED, letter);
}

export function onLetterArrived(
  game: Phaser.Game,
  cb: (letter: HudLetterPayload) => void,
): HudUnsub {
  game.events.on(HudEvents.LETTER_ARRIVED, cb);
  return () => game.events.off(HudEvents.LETTER_ARRIVED, cb);
}

export function emitLetterOpened(game: Phaser.Game, letter: HudLetterPayload): void {
  game.events.emit(HudEvents.LETTER_OPENED, letter);
}

export function onLetterOpened(
  game: Phaser.Game,
  cb: (letter: HudLetterPayload) => void,
): HudUnsub {
  game.events.on(HudEvents.LETTER_OPENED, cb);
  return () => game.events.off(HudEvents.LETTER_OPENED, cb);
}

export function emitLetterboxToggle(game: Phaser.Game): void {
  game.events.emit(HudEvents.LETTERBOX_TOGGLE);
}

export function onLetterboxToggle(game: Phaser.Game, cb: () => void): HudUnsub {
  game.events.on(HudEvents.LETTERBOX_TOGGLE, cb);
  return () => game.events.off(HudEvents.LETTERBOX_TOGGLE, cb);
}

// ── C5 almanac (Layer 3) ───────────────────────────────────────────────────

export function emitAlmanacToggle(game: Phaser.Game): void {
  game.events.emit(HudEvents.ALMANAC_TOGGLE);
}

export function onAlmanacToggle(game: Phaser.Game, cb: () => void): HudUnsub {
  game.events.on(HudEvents.ALMANAC_TOGGLE, cb);
  return () => game.events.off(HudEvents.ALMANAC_TOGGLE, cb);
}

export function emitAlmanacOpened(game: Phaser.Game): void {
  game.events.emit(HudEvents.ALMANAC_OPENED);
}

export function onAlmanacOpened(game: Phaser.Game, cb: () => void): HudUnsub {
  game.events.on(HudEvents.ALMANAC_OPENED, cb);
  return () => game.events.off(HudEvents.ALMANAC_OPENED, cb);
}

export function emitAlmanacClosed(game: Phaser.Game): void {
  game.events.emit(HudEvents.ALMANAC_CLOSED);
}

export function onAlmanacClosed(game: Phaser.Game, cb: () => void): HudUnsub {
  game.events.on(HudEvents.ALMANAC_CLOSED, cb);
  return () => game.events.off(HudEvents.ALMANAC_CLOSED, cb);
}

/**
 * Client-local journal entries (festival lifecycle, return digests) as the
 * almanac reads them.
 *
 * The key deliberately stays out of {@link HudRegistryKeys}: `resetHudState`
 * wipes every key listed there at the start of a run, and these entries are
 * owned by PaintedWorldCoordinator/JournalScene and must survive that. This
 * helper exists so panels still don't hand-type the string.
 */
export const JOURNAL_ENTRIES_REGISTRY_KEY = 'festival.journalEntries';

export interface HudJournalEntry {
  id: string;
  heading: string;
  body: string;
  timestamp: number;
}

export function readJournalEntries(game: Phaser.Game): HudJournalEntry[] {
  const raw = game.registry.get(JOURNAL_ENTRIES_REGISTRY_KEY) as
    | HudJournalEntry[]
    | undefined;
  return Array.isArray(raw) ? raw : [];
}

// ── Reset ───────────────────────────────────────────────────────────────────
/**
 * Clear every HudState-owned registry key. Call from a world scene's `init()`
 * when a fresh run begins (e.g. GameScene.init for a new dungeon) so panels
 * don't see stale state from a prior run.
 *
 * Safe to call with no keys registered — becomes a no-op today, grows as
 * panels migrate.
 */
export function resetHudState(game: Phaser.Game): void {
  const keys = Object.values(HudRegistryKeys) as readonly string[];
  for (const key of keys) {
    game.registry.remove(key);
  }
}
